/*!
 * Wrapper data action for OOB GetSalesOrderDetailsBySalesId.
 * Uses Commerce IActionContext to build a retail-proxy IContext.
 */
import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { SalesOrder, SearchLocation } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import {
    getSalesOrderDetailsBySalesIdAsync,
    getSalesOrderDetailsByTransactionIdAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/SalesOrdersDataActions.g';

export class GetSalesOrderDetailsBySalesIdInput implements IActionInput {
    public readonly salesId: string;

    public constructor(salesId: string) {
        this.salesId = salesId;
    }

    public getCacheKey = () => `GetSalesOrderDetailsBySalesId-${this.salesId}`;

    public getCacheObjectType = () => 'GetSalesOrderDetailsBySalesId';

    public dataCacheType = (): CacheType => 'none';
}

const createInput = (input: ICreateActionContext): GetSalesOrderDetailsBySalesIdInput => {
    const params = ((input.requestContext?.params ?? ({} as unknown)) as unknown) as Record<string, string | undefined>;
    const query = (input.requestContext?.query ?? ({} as unknown)) as Record<string, string | undefined>;
    const salesId = params.salesId ?? query.salesId ?? '';
    return new GetSalesOrderDetailsBySalesIdInput(salesId);
};

export async function getSalesOrderDetailsBySalesIdAction(
    input: GetSalesOrderDetailsBySalesIdInput,
    ctx: IActionContext
): Promise<SalesOrder> {
    if (!input.salesId) {
        throw new Error('[get-sales-order-details-by-sales-id] SalesId is required');
    }

    const retailProxyContext: import('@msdyn365-commerce/retail-proxy').IContext = {
        // Pass full IActionContext as callerContext so retail-proxy can resolve
        // required request parameters (locale, channel, catalog, etc.).
        callerContext: (ctx as unknown) as import('@msdyn365-commerce/retail-proxy').ICallerContext
    };

    try {
        const bySalesId = await getSalesOrderDetailsBySalesIdAsync(retailProxyContext, input.salesId);
        if (bySalesId) {
            return bySalesId;
        }
    } catch (error) {
        ctx.trace(`[get-sales-order-details-by-sales-id] bySalesId failed for "${input.salesId}"`);
    }

    // Fallback: some environments resolve this value as transaction id.
    try {
        const byTransactionIdRemote = await getSalesOrderDetailsByTransactionIdAsync(
            retailProxyContext,
            input.salesId,
            SearchLocation.Remote
        );
        if (byTransactionIdRemote) {
            return byTransactionIdRemote;
        }
    } catch (error) {
        ctx.trace(`[get-sales-order-details-by-sales-id] byTransactionId(remote) failed for "${input.salesId}"`);
    }

    try {
        const byTransactionIdLocal = await getSalesOrderDetailsByTransactionIdAsync(
            retailProxyContext,
            input.salesId,
            SearchLocation.Local
        );
        if (byTransactionIdLocal) {
            return byTransactionIdLocal;
        }
    } catch (error) {
        ctx.trace(`[get-sales-order-details-by-sales-id] byTransactionId(local) failed for "${input.salesId}"`);
    }

    throw new Error(`[get-sales-order-details-by-sales-id] No sales order found for "${input.salesId}"`);
}

export const getSalesOrderDetailsBySalesIdDataAction = createObservableDataAction({
    id: 'get-sales-order-details-by-sales-id',
    action: <IAction<SalesOrder>>getSalesOrderDetailsBySalesIdAction,
    input: createInput
});

export default getSalesOrderDetailsBySalesIdDataAction;

export const requestSalesOrderDetailsBySalesId = (ctx: IActionContext, salesId: string): Promise<SalesOrder> =>
    getSalesOrderDetailsBySalesIdAction(new GetSalesOrderDetailsBySalesIdInput(salesId), ctx);
